import { arrow, autoUpdate, FloatingArrow, offset, size, useFloating } from "@floating-ui/react";
import { IconClose } from "@tine/designsystem-icons/sharp";
import { type PropsWithChildren, useEffect, useRef } from "react";
import useOutsideClick from "../../common/useOutsideClick";
import Button from "../Button";
import MainColumn from "./MainColumn";

type ColumnDropdownProps = {
   open: boolean;
   onOpenChange: (open: boolean) => void;
   referenceElement: Element;
   withCloseButton?: boolean;
} & PropsWithChildren;

const ColumnDropdown = ({ open, onOpenChange, referenceElement, withCloseButton = false, children }: ColumnDropdownProps) => {
   const arrowRef = useRef<SVGSVGElement>(null);
   const dropdownContainerRef = useRef<HTMLDivElement>(null);
   const { refs, context, floatingStyles } = useFloating({
      placement: "bottom",
      open,
      onOpenChange,
      transform: false,

      elements: {
         reference: referenceElement
      },
      whileElementsMounted: autoUpdate,
      middleware: [
         offset(9),
         size({
            apply({ elements }) {
               const popupContainer = elements.floating.firstElementChild as HTMLDivElement;
               if (popupContainer) {
                  popupContainer.style.left = `${-elements.floating.getBoundingClientRect().left}px`;
               }
            }
         }),
         arrow({
            element: arrowRef
         })
      ]
   });
   useOutsideClick([dropdownContainerRef, referenceElement], () => {
      onOpenChange(false);
   });

   useEffect(() => {
      const keyDownHandler = (event: KeyboardEvent) => {
         if (event.key === "Escape" && open) {
            onOpenChange(false);
         }
      };
      window.addEventListener("keydown", keyDownHandler);
      return () => {
         window.removeEventListener("keydown", keyDownHandler);
      };
   }, [open, onOpenChange]);

   if (!open) {
      return null;
   }

   const arrowClasses = "fill-base-0 [&>path:first-of-type]:stroke-ink-border-subtle [&>path:last-of-type]:stroke-base-0";
   return (
      <div
         ref={refs.setFloating}
         className="left-0 top-0 absolute w-max !min-w-[100vw] flex z-50 bg-base-0 overflow-visible"
         style={floatingStyles}
      >
         <div className="absolute w-[100vw] left-0 top-[-1px]">
            <MainColumn>
               <div ref={dropdownContainerRef} className="w-full border rounded-lg border-ink-border-subtle bg-base-0">
                  {withCloseButton && (
                     <div className="flex justify-end w-full p-2">
                        <Button variant="tertiary" onClick={() => onOpenChange(false)} icon={<IconClose />} />
                     </div>
                  )}
                  {children}
               </div>
            </MainColumn>
         </div>
         <FloatingArrow ref={arrowRef} context={context} className={arrowClasses} strokeWidth={1} />
      </div>
   );
};

export default ColumnDropdown;
